import { EventEmitter, Injectable, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CardAction, CardDefinition, CardModel } from '@barthauer/abc/cards-view';

import { AbcCore } from '@barthauer/abc/core';
import { ExecuteQueryDocumentDef, QueryObjectsModel } from '@barthauer/abc/core/data';
import {
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { AbcQueriesEditorService } from '@barthauer/abc/queries/opath/queries-editor';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { AbcContainerService, AbcWindowComponent, IWindow, WindowStyle } from '@barthauer/abc/ui-components';
import { Observable, Subject } from 'rxjs';
import { BceComponent } from '../bce.component';
import { BCEDocumentBase, BCEDocumentType, BCEGroupPacket, BCEOpenMode } from '../models/bce.model';
import { DocumentCardBase, GroupCard } from '../viewModels/bce.viewModel';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root',
})
export class BceService {
  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) abcWindow: ViewContainerRef;
  private windowInstance: AbcWindowComponent;
  private groupPacketParameter: BCEGroupPacket[];

  public cardModel: CardModel;
  public cardModelData: Subject<CardModel> = new Subject<CardModel>();
  @Output() selectDocumentCancelled = new EventEmitter();

  constructor(
    private abcContainer: AbcContainerService,
    private dataService: DataService,
    private abcCore: AbcCore,
    private queriesEditorService: AbcQueriesEditorService,
    private messageBoxService: MessageBoxService,
    sanitizer: DomSanitizer,
    iconRegistry: MatIconRegistry
  ) {
    iconRegistry.addSvgIcon('Opathquery', sanitizer.bypassSecurityTrustResourceUrl('assets/iconApplication.svg'));
  }

  public bceOpen(groupPacket?: BCEGroupPacket | BCEGroupPacket[], isMobile?: boolean): void {
    if (!isMobile == true) {
      this.openAbcWindow();
    }
    const convertOArray = (v) => [].concat(v);
    this.groupPacketParameter = typeof groupPacket === 'string' ? convertOArray(groupPacket) : groupPacket;
    this.dataService.getGroupsCardData(this.groupPacketParameter).subscribe((groups: GroupCard[]) => {
      this.cardModel = new CardModel(groups);
      this.cardModelData.next(this.cardModel);
      if (this.windowInstance) this.windowInstance.showLoader = false;

      const primaryDocumentCardAction: CardAction = new CardAction('Edit', async (cardDefinition) => {
        await this.editDocument(<BCEDocumentBase>cardDefinition.DataObject);
      });

      this.setPrimaryActionOnDocuments(this.cardModel.CardDefinitions, primaryDocumentCardAction);
      const secondaryDocumentCardAction: CardAction = new CardAction('Execute', async (cardDefinition) => {
        await this.executeDocument(<BCEDocumentBase>cardDefinition.DataObject);
      });

      this.setSecondaryActionOnDocuments(this.cardModel.CardDefinitions, secondaryDocumentCardAction);
    });
  }
  public selectDocument(
    groupPacket?: BCEGroupPacket | BCEGroupPacket[],
    isMobile?: boolean,
    xPathFilter?: string,
    folderId?: string,
    allowTemplates?: boolean
  ): Observable<BCEDocumentBase> {
    if (!isMobile == true) {
      this.openAbcWindow();
    }
    const convertOArray = (v) => [].concat(v);
    this.groupPacketParameter = typeof groupPacket === 'string' ? convertOArray(groupPacket) : groupPacket;
    const observable$ = new Observable<BCEDocumentBase>((observer) => {
      this.dataService
        .getGroupsCardData(this.groupPacketParameter, folderId, xPathFilter, allowTemplates)
        .subscribe((groups: GroupCard[]) => {
          this.cardModel = new CardModel(groups);
          this.cardModelData.next(this.cardModel);
          if (this.windowInstance) this.windowInstance.showLoader = false;

          const primaryDocumentCardAction: CardAction = new CardAction('Select', (cardDefinition) => {
            observer.next(<BCEDocumentBase>cardDefinition.DataObject);
            if (this.windowInstance) this.windowInstance.closeDialog();
          });
          this.setPrimaryActionOnDocuments(this.cardModel.CardDefinitions, primaryDocumentCardAction);

          const secondaryDocumentCardAction: CardAction = new CardAction('Execute', async (cardDefinition) => {
            await this.executeDocument(<BCEDocumentBase>cardDefinition.DataObject);
          });

          this.setSecondaryActionOnDocuments(this.cardModel.CardDefinitions, secondaryDocumentCardAction);
        });
    });
    return observable$;
  }

  private setPrimaryActionOnDocuments(viewModel: CardDefinition[], primaryDocumentCardAction: CardAction): void {
    const mode = primaryDocumentCardAction.DisplayText;
    viewModel.forEach((element) => {
      if (element instanceof DocumentCardBase) {
        element.PrimaryAction = primaryDocumentCardAction;
      } else {
        if (element.CardDefinitions) {
          this.setPrimaryActionOnDocuments(element.CardDefinitions, primaryDocumentCardAction);
        }
      }
    });
  }

  private setSecondaryActionOnDocuments(viewModel: CardDefinition[], secondaryDocumentCardAction: CardAction): void {
    viewModel.forEach((element) => {
      if (element instanceof DocumentCardBase) {
        element.SecondaryAction = secondaryDocumentCardAction;
      } else {
        if (element.CardDefinitions) {
          this.setSecondaryActionOnDocuments(element.CardDefinitions, secondaryDocumentCardAction);
        }
      }
    });
  }

  public callCloseDialog(): void {
    if (this.windowInstance) this.windowInstance.closeDialog();
  }

  private openAbcWindow(): void {
    const style: WindowStyle = {
      height: '60%',
      minWidth: '400px',
      width: '600px',
      resizable: true,
      shadingColor: '#ffffff80',
      shading: true,
    };
    const WindowAttr: IWindow = {
      id: 'BCEwindow',
      label: 'Barthauer Configuration Explorer',
      maximizeButton: true,
      minimizeButton: true,
      showLoader: true,
      minumizeDialogOption: -64,
      component: BceComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };
    this.createWindowInstance(WindowAttr);
  }
  private createWindowInstance(WindowAttr: IWindow): void {
    let windowComponentInstance = null;
    const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
    this.windowInstance = <AbcWindowComponent>window.componentRef.instance;
    this.windowInstance.createComponentInstance().subscribe((activeComponent) => {
      windowComponentInstance = activeComponent;
      activeComponent.openWindow();
    });

    windowComponentInstance.onCloseClick.subscribe(() => {
      this.selectDocumentCancelled.emit();
      this.windowInstance.showLoader = false;
    });
  }

  private async editDocument(doc: BCEDocumentBase): Promise<void> {
    await this.openDocumentCore(doc, BCEOpenMode.Edit);
  }

  public async executeDocument(doc: BCEDocumentBase): Promise<void> {
    await this.openDocumentCore(doc, BCEOpenMode.Execute);
  }

  private async openDocumentCore(doc: BCEDocumentBase, mode: BCEOpenMode): Promise<void> {
    if (doc.documentType === BCEDocumentType.OPathQueryDocument) {
      if (await this.isDmlQueryDocument(doc.Id)) {
        if (mode === BCEOpenMode.Execute) {
          await this.executeDmlQueryDocument(doc.Id, doc.Designation);
        } else {
          throw new Error('Editing DML query documents is not supported!');
        }
      } else {
        await this.openOPathQueryDocument(doc.Id, doc.Designation, mode);
      }
    } else {
      throw new Error('Invalid document type: ' + doc.documentType);
    }
  }

  private async openOPathQueryDocument(documentId: string, title: string, mode: BCEOpenMode): Promise<void> {
    try {
      await this.queriesEditorService.runOpathQueriesEditor(documentId, title, mode === BCEOpenMode.Execute);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  public async isDmlQueryDocument(documentId: string): Promise<boolean> {
    if (!documentId) {
      return false;
    }
    const ids = await this.dataService.queryFilterDocuments([documentId], "//ObjectQueryDocument[@dmlType!='']");
    return await ids?.includes(documentId);
  }

  public async filterDocuments(documentIds: string[], xpathFilter: string): Promise<string[]> {
    return await this.dataService.queryFilterDocuments(documentIds, xpathFilter);
  }

  public async executeOPathQuery(query: QueryObjectsModel, title: string) {
    try {
      await this.queriesEditorService.executeQuery(query, title);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  public async executeDmlQueryDocument(
    documentId: string,
    title?: string,
    parameters?: ParameterValue[]
  ): Promise<void> {
    const executeQueryDocumentDef: ExecuteQueryDocumentDef = new ExecuteQueryDocumentDef();
    executeQueryDocumentDef.Id = documentId;
    executeQueryDocumentDef.Parameters = parameters;

    const results = await this.abcCore.data.executeQueryDocument(executeQueryDocumentDef).toPromise();
    await this.showMessageAsHtml(await this.formatDmlResults(results), title);
  }

  public async executeDmlQuery(query: QueryObjectsModel, title?: string): Promise<void> {
    const results = await this.abcCore.data.executeManipulateObjectsQuery(query).toPromise();
    await this.showMessageAsHtml(await this.formatDmlResults(results), title);
  }

  private async formatDmlResults(results: { [key: string]: string[] }): Promise<string> {
    const res = await this.dataService.getResourceSet();
    let ret = res.get('Execute-NoData');
    if (results) {
      let msgs = '';
      const keys = Object.keys(results);
      for (const key of keys) {
        const objArray = results[key];
        if (objArray) {
          if (!msgs) msgs = key + ': ' + objArray.length + '<br/>';
          else msgs += key + ': ' + objArray.length + '<br/>';
        }
      }
      ret = res.get('Execute-Success') + '<br/><br/>' + msgs;
    }

    return ret;
  }

  private async showMessageAsHtml(htmlMsg: string, title: string): Promise<void> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Info;
    settings.icon = MessageBoxIcon.Info;
    settings.button = MessageBoxButtons.OK;
    settings.caption = title;

    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;
    await this.messageBoxService.showMessageBox(settings).toPromise();
  }
}
